<template>
  <div class="credit-card card">
    <div class="credit-card-stripe"></div>
    <div class="credit-card-layout">
      <div class="credit-card-info">
        <div class="credit-card-info-cardholder">
          {{ card.cardholder_name }}
        </div>
        <div class="credit-card-info-number">
          {{ formattedCardNumber }}
        </div>
        <div class="credit-card-info-expiration">
          <div class="credit-card-info-expiration-label">Valid<br />Thru</div>
          <div class="credit-card-info-expiration-value">
            {{ card.expiration_month }}/{{ card.expiration_year }}
          </div>
        </div>
      </div>
      <div class="credit-card-cvv">
        <slot name="cvv">
          <div class="credit-card-cvv-label">
            CVV
          </div>
          <div class="credit-card-cvv-value">
            {{ formattedCvv }}
            <b-tooltip
              v-if="hasCvvExpiration"
              :label="cvvExplanation"
              type="is-black"
              multilined
              :position="isMobile ? 'is-left' : 'is-top'"
            >
              <b-icon icon="help-circle" type="is-info" size="is-small" />
            </b-tooltip>
          </div>
        </slot>
      </div>
    </div>
    <img v-if="cardImage" class="credit-card-icon" :src="cardImage" alt="" />
  </div>
</template>

<script>
import getCreditCardType from "@/helpers/getCreditCardType";
import creditCardIcons from "@/constants/creditCardIcons";
import {
  formatCreditCardNumber,
  maskCreditCardNumber
} from "@/helpers/creditCardNumber";
import NowMixin from "@/mixins/now";
import DateTimeMixin from "@/mixins/dateTime";
import { TABLET } from "@/constants/breakpoints";

export default {
  mixins: [DateTimeMixin, NowMixin],
  props: {
    card: {
      type: Object,
      required: true
    },
    cardNumber: {
      type: String,
      default: null
    },
    cvv: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isMobile: window.innerWidth < TABLET
    };
  },
  computed: {
    formattedCardNumber() {
      return this.cardNumber
        ? formatCreditCardNumber(this.cardNumber)
        : maskCreditCardNumber(this.card.card_length, this.card.last_four);
    },
    hasCvvExpiration() {
      return this.card.cvv_expires_at;
    },
    hasCvvExpired() {
      return this.hasCvvExpiration
        ? new Date(this.card.cvv_expires_at) < this.now
        : false;
    },
    cvvExplanation() {
      if (!this.hasCvvExpiration) {
        return "";
      }
      const explanation =
        "Access to CVV is restricted 48 hours after receiving client approval";
      if (this.hasCvvExpired) {
        return explanation;
      }
      const countdown = this.getFormattedTimeDelta(
        new Date(this.card.cvv_expires_at).getTime(),
        this.now.getTime()
      );
      return `${explanation}. It will be restricted in ${countdown}.`;
    },
    formattedCvv() {
      if (this.cvv) {
        return this.cvv;
      }
      return this.hasCvvExpired ? "N/A" : "•••";
    },
    cardImage() {
      const type = getCreditCardType(this.card.first_six);
      return creditCardIcons[type];
    }
  }
};
</script>

<style lang="scss" scoped>
@import "bulma/sass/utilities/mixins.sass";

.credit-card {
  border-radius: 15px;
  font-family: "Courier New", Courier, monospace;
  font-size: 18px;
  padding: 25px 0 50px;
  position: relative;
  @include tablet {
    min-height: 225px;
  }
  @include mobile {
    font-size: 15px;
  }

  &-stripe {
    background: rgba(#000, 0.7);
    height: 45px;
  }
  &-layout {
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 15px 20px 0 20px;
  }
  &-info {
    display: flex;
    flex-direction: column;
    margin-right: 10px;
    text-transform: uppercase;
    &-expiration {
      align-items: center;
      display: flex;
      flex-direction: row;
      &-label {
        font-size: 10px;
        line-height: 10px;
      }
      &-value {
        margin-left: 10px;
      }
    }
  }
  &-cvv {
    align-items: center;
    display: flex;
    flex-direction: column;
    &-label {
      font-size: 10px;
      line-height: 12px;
      margin-top: -12px;
    }
    &-value {
      align-items: center;
      display: flex;
      flex-direction: row;
      .b-tooltip {
        font-family: Lato, sans-serif;
        margin-left: 5px;
      }
    }
  }
  &-icon {
    bottom: 15px;
    height: 50px;
    position: absolute;
    right: 20px;
    @include mobile {
      height: 40px;
    }
  }
}
</style>
